import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { ImageInfo } from '../../../services/image-processing-tool.service';

@Component({
  selector: 'app-image-viewer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSliderModule
  ],
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss']
})
export class ImageViewerComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() image: ImageInfo | null = null;
  @Input() cropMode: boolean = false;
  @Input() markMode: 'vertical' | 'horizontal' | null = null;
  @Input() panMode: boolean = false;
  @Input() zoomLevel: number = 1.0;
  
  @Output() cutLine = new EventEmitter<number>();
  @Output() cropRect = new EventEmitter<{ x: number; y: number; width: number; height: number }>();
  @Output() lineMarked = new EventEmitter<{ type: 'vertical' | 'horizontal'; position: number }>();
  @Output() debugLog = new EventEmitter<string>();
  
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('container') containerRef!: ElementRef<HTMLDivElement>;
  
  private ctx: CanvasRenderingContext2D | null = null;
  private loadedImage: HTMLImageElement | null = null;
  
  // Marked lines (for cut operations)
  private markedLines: { type: 'vertical' | 'horizontal'; position: number }[] = [];
  
  // Zoom and Pan
  zoom = 1;
  minZoom = 0.1;
  maxZoom = 5;
  panX = 0;
  panY = 0;
  
  // Dragging state
  isDragging = false;
  dragStartX = 0;
  dragStartY = 0;
  lastPanX = 0;
  lastPanY = 0;
  
  // Line drawing (for cut)
  isDrawingLine = false;
  lineStartY = 0;
  currentLineY = 0;
  showCutLine = false;
  
  // Crop rectangle drawing
  isDrawingCrop = false;
  cropStartX = 0;
  cropStartY = 0;
  cropEndX = 0;
  cropEndY = 0;
  showCropRect = false;
  
  // Canvas dimensions
  canvasWidth = 0;
  canvasHeight = 0;
  
  // Image dimensions
  imageWidth = 0;
  imageHeight = 0;
  
  constructor() {}
  
  ngOnInit(): void {}
  
  /**
   * Get cursor style based on current mode
   * Like Python UI - crosshair only when mark mode or crop mode is active
   */
  getCursorStyle(): string {
    if (this.cropMode) {
      return 'crosshair';
    }
    if (this.markMode) {
      return 'crosshair';
    }
    if (this.panMode) {
      return 'grab';
    }
    // Default cursor when no mode is selected
    return 'default';
  }
  
  ngOnDestroy(): void {
    this.loadedImage = null;
  }
  
  ngAfterViewInit(): void {
    this.initCanvas();
    if (this.image) {
      this.loadImage();
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['image'] && this.canvasRef) {
      this.resetView();
      this.markedLines = []; // Clear marked lines on new image
      this.loadImage();
    }
    
    if (changes['cropMode']) {
      this.showCropRect = false;
      this.render();
    }
    
    if (changes['zoomLevel'] && this.canvasRef) {
      this.zoom = this.zoomLevel;
      this.render();
    }
  }
  
  private initCanvas(): void {
    const canvas = this.canvasRef?.nativeElement;
    const container = this.containerRef?.nativeElement;
    
    if (!canvas || !container) return;
    
    this.ctx = canvas.getContext('2d');
    this.resizeCanvas();
    
    // Add resize observer
    const resizeObserver = new ResizeObserver(() => {
      this.resizeCanvas();
      this.render();
    });
    resizeObserver.observe(container);
  }
  
  /**
   * Resize canvas with proper devicePixelRatio for sharp image rendering
   * This matches Python UI's Qt.SmoothTransformation quality
   */
  private resizeCanvas(): void {
    const canvas = this.canvasRef?.nativeElement;
    const container = this.containerRef?.nativeElement;
    
    if (!canvas || !container) return;
    
    const rect = container.getBoundingClientRect();
    this.canvasWidth = rect.width;
    this.canvasHeight = rect.height;
    
    // Use devicePixelRatio for sharp rendering on high-DPI displays
    // This is the key to matching Python's Qt.SmoothTransformation quality
    const dpr = window.devicePixelRatio || 1;
    
    // Set actual canvas size in memory (scaled by dpr)
    canvas.width = Math.floor(this.canvasWidth * dpr);
    canvas.height = Math.floor(this.canvasHeight * dpr);
    
    // Scale canvas back to CSS size
    canvas.style.width = `${this.canvasWidth}px`;
    canvas.style.height = `${this.canvasHeight}px`;
    
    // Get fresh context and scale for high-DPI
    this.ctx = canvas.getContext('2d');
    if (this.ctx) {
      // Reset transform and scale for dpr
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }
  
  private loadImage(): void {
    if (!this.image?.url) {
      this.loadedImage = null;
      this.render();
      return;
    }
    
    this.debugLog.emit(`[Viewer] Loading: ${this.image.filename || 'image'}`);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      this.loadedImage = img;
      this.imageWidth = img.width;
      this.imageHeight = img.height;
      this.fitToView();
      this.debugLog.emit(`[Viewer] Loaded: ${img.width}x${img.height}`);
    };
    
    img.onerror = () => {
      this.debugLog.emit(`[Viewer] ERROR: Failed to load image from ${this.image?.url}`);
      this.loadedImage = null;
      this.imageWidth = 0;
      this.imageHeight = 0;
      this.render();
    };
    
    img.src = this.image.url;
  }
  
  private fitToView(): void {
    if (!this.loadedImage) return;
    
    const containerWidth = this.canvasWidth;
    const containerHeight = this.canvasHeight;
    
    const scaleX = containerWidth / this.loadedImage.width;
    const scaleY = containerHeight / this.loadedImage.height;
    
    this.zoom = Math.min(scaleX, scaleY) * 0.95; // 95% to leave some padding
    this.panX = (containerWidth - this.loadedImage.width * this.zoom) / 2;
    this.panY = (containerHeight - this.loadedImage.height * this.zoom) / 2;
    
    this.render();
  }
  
  resetView(): void {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.showCutLine = false;
    this.showCropRect = false;
  }
  
  // ==================== Rendering ====================
  
  private render(): void {
    if (!this.ctx) return;
    
    // Clear canvas with white background (matching Python UI)
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
    
    // Draw image with high quality rendering (like Python's Qt.SmoothTransformation)
    if (this.loadedImage) {
      this.ctx.save();
      
      // Enable high-quality image rendering for sharp thumbnails and work-area display
      // This matches Python UI's Qt.SmoothTransformation quality
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = 'high';
      
      this.ctx.translate(this.panX, this.panY);
      this.ctx.scale(this.zoom, this.zoom);
      this.ctx.drawImage(this.loadedImage, 0, 0);
      this.ctx.restore();
    }
    
    // Draw all marked lines
    this.drawMarkedLines();
    
    // Draw cut line preview
    if (this.showCutLine && !this.cropMode) {
      this.drawCutLine();
    }
    
    // Draw crop rectangle
    if (this.showCropRect && this.cropMode) {
      this.drawCropRectangle();
    }
    
    // Draw image border
    if (this.loadedImage) {
      this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
      this.ctx.lineWidth = 1;
      this.ctx.strokeRect(
        this.panX,
        this.panY,
        this.loadedImage.width * this.zoom,
        this.loadedImage.height * this.zoom
      );
    }
  }
  
  private drawMarkedLines(): void {
    if (!this.ctx || !this.loadedImage || this.markedLines.length === 0) return;
    
    this.ctx.save();
    this.ctx.strokeStyle = '#F44336'; // Red color for marked lines
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([5, 3]);
    
    for (const line of this.markedLines) {
      if (line.type === 'horizontal') {
        const y = this.panY + line.position * this.zoom;
        this.ctx.beginPath();
        this.ctx.moveTo(this.panX, y);
        this.ctx.lineTo(this.panX + this.loadedImage.width * this.zoom, y);
        this.ctx.stroke();
      } else {
        const x = this.panX + line.position * this.zoom;
        this.ctx.beginPath();
        this.ctx.moveTo(x, this.panY);
        this.ctx.lineTo(x, this.panY + this.loadedImage.height * this.zoom);
        this.ctx.stroke();
      }
    }
    
    this.ctx.restore();
  }
  
  private drawCutLine(): void {
    if (!this.ctx) return;
    
    // Horizontal line across image
    this.ctx.strokeStyle = '#ff4444';
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([5, 5]);
    this.ctx.beginPath();
    this.ctx.moveTo(0, this.currentLineY);
    this.ctx.lineTo(this.canvasWidth, this.currentLineY);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
    
    // Line position indicator
    const imageY = this.canvasToImageY(this.currentLineY);
    this.ctx.fillStyle = '#ff4444';
    this.ctx.font = '12px Arial';
    this.ctx.fillText(`Cut at Y: ${Math.round(imageY)}px`, 10, this.currentLineY - 10);
  }
  
  private drawCropRectangle(): void {
    if (!this.ctx) return;
    
    const x = Math.min(this.cropStartX, this.cropEndX);
    const y = Math.min(this.cropStartY, this.cropEndY);
    const width = Math.abs(this.cropEndX - this.cropStartX);
    const height = Math.abs(this.cropEndY - this.cropStartY);
    
    // Semi-transparent overlay outside crop area
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    this.ctx.fillRect(0, 0, this.canvasWidth, y);
    this.ctx.fillRect(0, y, x, height);
    this.ctx.fillRect(x + width, y, this.canvasWidth - x - width, height);
    this.ctx.fillRect(0, y + height, this.canvasWidth, this.canvasHeight - y - height);
    
    // Crop rectangle border
    this.ctx.strokeStyle = '#00ff00';
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([5, 5]);
    this.ctx.strokeRect(x, y, width, height);
    this.ctx.setLineDash([]);
    
    // Dimension indicator
    const imgRect = this.canvasCropToImageCoords();
    this.ctx.fillStyle = '#00ff00';
    this.ctx.font = '12px Arial';
    this.ctx.fillText(
      `Crop: ${Math.round(imgRect.width)}x${Math.round(imgRect.height)}`,
      x + 5,
      y - 5
    );
  }
  
  // ==================== Coordinate Conversion ====================
  
  private canvasToImageX(canvasX: number): number {
    return (canvasX - this.panX) / this.zoom;
  }
  
  private canvasToImageY(canvasY: number): number {
    return (canvasY - this.panY) / this.zoom;
  }
  
  private canvasCropToImageCoords(): { x: number; y: number; width: number; height: number } {
    const x1 = this.canvasToImageX(Math.min(this.cropStartX, this.cropEndX));
    const y1 = this.canvasToImageY(Math.min(this.cropStartY, this.cropEndY));
    const x2 = this.canvasToImageX(Math.max(this.cropStartX, this.cropEndX));
    const y2 = this.canvasToImageY(Math.max(this.cropStartY, this.cropEndY));
    
    return {
      x: Math.max(0, x1),
      y: Math.max(0, y1),
      width: Math.min(this.imageWidth, x2) - Math.max(0, x1),
      height: Math.min(this.imageHeight, y2) - Math.max(0, y1)
    };
  }
  
  // ==================== Mouse Events ====================
  
  onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    if (this.cropMode) {
      // Start crop rectangle
      this.debugLog.emit(`[VIEWER] Starting crop at (${Math.round(x)}, ${Math.round(y)})`);
      this.isDrawingCrop = true;
      this.cropStartX = x;
      this.cropStartY = y;
      this.cropEndX = x;
      this.cropEndY = y;
      this.showCropRect = true;
    } else if (this.markMode) {
      // Mark a line at click position
      this.debugLog.emit(`[VIEWER] Marking ${this.markMode} line at position`);
      this.addMarkedLine(x, y);
    } else if (this.panMode || event.button === 1 || event.button === 2) {
      // Pan mode or middle/right click - pan
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.lastPanX = this.panX;
      this.lastPanY = this.panY;
    }
    // Note: Removed direct cut line drawing on left-click without mark mode
    // User must explicitly select horizontal or vertical mark mode first (like Python UI)
    
    this.render();
  }
  
  private addMarkedLine(canvasX: number, canvasY: number): void {
    if (!this.loadedImage) {
      this.debugLog.emit(`[VIEWER] Cannot mark line - no image loaded`);
      return;
    }
    
    let position: number;
    if (this.markMode === 'horizontal') {
      position = this.canvasToImageY(canvasY);
      if (position >= 0 && position <= this.imageHeight) {
        this.markedLines.push({ type: 'horizontal', position });
        this.lineMarked.emit({ type: 'horizontal', position: Math.round(position) });
        this.debugLog.emit(`[VIEWER] Marked horizontal line at Y=${Math.round(position)} (image coords)`);
      } else {
        this.debugLog.emit(`[VIEWER] Line position out of bounds: Y=${Math.round(position)}, imageHeight=${this.imageHeight}`);
      }
    } else if (this.markMode === 'vertical') {
      position = this.canvasToImageX(canvasX);
      if (position >= 0 && position <= this.imageWidth) {
        this.markedLines.push({ type: 'vertical', position });
        this.lineMarked.emit({ type: 'vertical', position: Math.round(position) });
        this.debugLog.emit(`[VIEWER] Marked vertical line at X=${Math.round(position)} (image coords)`);
      } else {
        this.debugLog.emit(`[VIEWER] Line position out of bounds: X=${Math.round(position)}, imageWidth=${this.imageWidth}`);
      }
    }
    this.render();
  }
  
  clearMarkedLines(): void {
    const count = this.markedLines.length;
    this.markedLines = [];
    this.debugLog.emit(`[VIEWER] Cleared ${count} marked lines`);
    this.render();
  }
  
  onMouseMove(event: MouseEvent): void {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    if (this.isDrawingCrop) {
      this.cropEndX = x;
      this.cropEndY = y;
      this.render();
    } else if (this.isDrawingLine) {
      this.currentLineY = y;
      this.render();
    } else if (this.isDragging) {
      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      this.panX = this.lastPanX + deltaX;
      this.panY = this.lastPanY + deltaY;
      this.render();
    }
  }
  
  onMouseUp(event: MouseEvent): void {
    if (this.isDrawingCrop) {
      this.isDrawingCrop = false;
      const cropRect = this.canvasCropToImageCoords();
      
      if (cropRect.width > 10 && cropRect.height > 10) {
        this.debugLog.emit(`[VIEWER] Crop completed: x=${Math.round(cropRect.x)}, y=${Math.round(cropRect.y)}, w=${Math.round(cropRect.width)}, h=${Math.round(cropRect.height)}`);
        this.cropRect.emit(cropRect);
      } else {
        this.debugLog.emit(`[VIEWER] Crop too small (${Math.round(cropRect.width)}x${Math.round(cropRect.height)}), ignoring`);
      }
      this.showCropRect = false;
    } else if (this.isDrawingLine) {
      this.isDrawingLine = false;
      const imageY = this.canvasToImageY(this.currentLineY);
      
      if (imageY > 0 && imageY < this.imageHeight) {
        this.debugLog.emit(`[VIEWER] Cut line completed at Y=${Math.round(imageY)} (imageHeight=${this.imageHeight})`);
        this.cutLine.emit(Math.round(imageY));
      } else {
        this.debugLog.emit(`[VIEWER] Cut line out of bounds: Y=${Math.round(imageY)}, imageHeight=${this.imageHeight}`);
      }
      this.showCutLine = false;
    }
    
    this.isDragging = false;
    this.render();
  }
  
  onMouseLeave(event: MouseEvent): void {
    this.isDragging = false;
    this.isDrawingLine = false;
    this.isDrawingCrop = false;
  }
  
  onWheel(event: WheelEvent): void {
    event.preventDefault();
    
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    // Calculate zoom
    const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom * zoomFactor));
    
    if (newZoom !== this.zoom) {
      // Zoom towards mouse position
      const zoomRatio = newZoom / this.zoom;
      this.panX = mouseX - (mouseX - this.panX) * zoomRatio;
      this.panY = mouseY - (mouseY - this.panY) * zoomRatio;
      this.zoom = newZoom;
      
      this.render();
    }
  }
  
  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }
  
  // ==================== Toolbar Actions ====================
  
  zoomIn(): void {
    this.zoom = Math.min(this.maxZoom, this.zoom * 1.25);
    this.centerAfterZoom();
    this.render();
  }
  
  zoomOut(): void {
    this.zoom = Math.max(this.minZoom, this.zoom * 0.8);
    this.centerAfterZoom();
    this.render();
  }
  
  zoomFit(): void {
    this.fitToView();
  }
  
  zoomActual(): void {
    this.zoom = 1;
    this.panX = (this.canvasWidth - this.imageWidth) / 2;
    this.panY = (this.canvasHeight - this.imageHeight) / 2;
    this.render();
  }
  
  private centerAfterZoom(): void {
    if (!this.loadedImage) return;
    
    const imageDisplayWidth = this.loadedImage.width * this.zoom;
    const imageDisplayHeight = this.loadedImage.height * this.zoom;
    
    // Keep image centered if it fits, otherwise constrain panning
    if (imageDisplayWidth < this.canvasWidth) {
      this.panX = (this.canvasWidth - imageDisplayWidth) / 2;
    }
    if (imageDisplayHeight < this.canvasHeight) {
      this.panY = (this.canvasHeight - imageDisplayHeight) / 2;
    }
  }
  
  get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }
}
